export interface FallbackSEO {
  title?: string;
  description?: string;
  keywords?: string;
}

// Static SEO. Single source of truth. Do NOT override from CMS.
// Titles are 59 characters or fewer. Descriptions are 154 characters or fewer.
// No dashes, no "&", and no digits. The brand form is "Brown Tech".
const FALLBACKS: Record<string, FallbackSEO> = {
  '/': {
    title: 'GoHighLevel Agency and AI Voice Agents | Brown Tech',
    description: 'Brown Tech builds GoHighLevel systems, AI voice agents and business automation for agencies and service businesses in the US and UK. Book a strategy call.',
    keywords: 'ai software development company, custom software development, ai development services, custom ai solutions, software development company usa, ai engineering team, machine learning development',
  },
  '/about': {
    title: 'About Brown Tech | GoHighLevel and AI Automation Agency',
    description: 'Meet the Brown Tech team behind GoHighLevel builds, AI voice agents and custom software for agencies and businesses in the US, UK and beyond.',
    keywords: 'software development company usa, about us technology company, us software company, ai first engineering team, software outsourcing company usa, technology partner usa',
  },
  '/contact': {
    title: 'Contact Brown Tech | Book a GoHighLevel or AI Strategy Call',
    description: 'Tell us what you want automated. Brown Tech replies with a scoped plan for your GoHighLevel, AI voice agent, automation or software project.',
    keywords: 'hire software developers, hire developers, hire software engineers, dedicated development team, software developers for hire usa, get a developer quote',
  },
  '/portfolio': {
    title: 'Software Development Portfolio | Brown Tech',
    description: 'Selected Brown Tech projects across AI, web, mobile and cloud, with the problem each client had and what we built. Browse the portfolio.',
    keywords: 'software development portfolio, software development case studies, custom software projects, ai software portfolio, mobile app case studies, tech company portfolio',
  },
  '/blogs': {
    title: 'GoHighLevel and AI Automation Blog | Brown Tech',
    description: 'Practical guides on GoHighLevel setup, GHL workflows, AI voice agents and business automation from the Brown Tech delivery team.',
    keywords: 'software development blog, ai blog, technology blog, software engineering articles, ai development insights, tech news, machine learning blog',
  },
  '/hiring': {
    title: 'Remote Software Developer Jobs | Brown Tech',
    description: 'Open roles at Brown Tech for developers, GoHighLevel specialists and AI engineers. See current positions, how we work and how to apply.',
    keywords: 'software developer jobs remote, remote software developer jobs, tech careers, remote developer positions, software engineering jobs, ai company careers, remote engineering roles',
  },
  '/capabilities/ai': {
    title: 'AI Automation and AI Development Services | Brown Tech',
    description: 'Brown Tech designs AI agents, LLM apps and workflow automation that remove manual work. Scoped up front, shipped in sprints. Book a discovery call.',
    keywords: 'ai development services, ai software development, machine learning development, llm development, ai automation, custom ai solutions, generative ai development, computer vision development',
  },
  '/capabilities/ai-sales': {
    title: 'AI Sales Chat and Outreach Agents | Brown Tech',
    description: 'AI sales agents from Brown Tech that qualify leads, answer questions and send follow ups through chat and email, connected to your CRM.',
    keywords: 'ai sales agent, ai voice agent, ai sales automation, custom ai sales agent, ai lead qualification, ai receptionist, conversational ai sales, ai appointment booking',
  },
  '/capabilities/mobile-development': {
    title: 'Mobile App Development Company | Brown Tech',
    description: 'Brown Tech designs and builds iOS, Android and cross platform apps, from first release to ongoing updates. Start your mobile project today.',
    keywords: 'mobile app development company, ios app development, android app development, react native development, flutter app development, mobile app development services, cross platform app development',
  },
  '/capabilities/web-development': {
    title: 'Custom Web Application Development | Brown Tech',
    description: 'Brown Tech builds fast, scalable web applications with React, Node.js and cloud hosting, from MVP to production. Get a quote for your web app.',
    keywords: 'custom web application development, web app development, saas development, react development, nodejs development, web application development services, custom web app company',
  },
  '/capabilities/ui-ux': {
    title: 'UI UX Design Services | Brown Tech',
    description: 'UI UX design from Brown Tech: user research, wireframes, prototypes and usability testing that turn into interfaces people finish. See our work.',
    keywords: 'ui ux design services, product design, ux design services, ui design company, figma design services, user experience design, prototyping services, usability testing',
  },
  '/capabilities/cloud-solution': {
    title: 'Cloud Migration and Cloud Computing Services | Brown Tech',
    description: 'Brown Tech migrates, modernises and manages workloads on AWS, Azure and Google Cloud with careful planning and monitoring. Talk to an engineer.',
    keywords: 'cloud computing services, cloud migration services, aws cloud services, azure cloud migration, google cloud services, managed cloud services, cloud infrastructure, cloud modernization',
  },
  '/services/staff-augmentation': {
    title: 'Staff Augmentation Services | Brown Tech',
    description: 'Add vetted Brown Tech engineers, designers and QA to your team without the recruitment overhead. Flexible terms, your process, your tools.',
    keywords: 'staff augmentation, it staff augmentation, extended development team, extended software development team, extended team model, team extension services, staff augmentation services usa, dedicated development team outsourcing',
  },
  '/services/product-studio': {
    title: 'Product Development Studio | Brown Tech',
    description: 'Launch a product with a Brown Tech studio team covering discovery, UX design and engineering, delivered in focused sprints. Book a call.',
    keywords: 'product development services, software product studio, product design and development, full cycle product development, product team outsourcing, mvp development services',
  },
  '/services/project-sprints': {
    title: 'Agile Software Development Sprints | Brown Tech',
    description: 'Fixed scope agile sprints from Brown Tech for audits, prototypes, migrations and AI proofs of concept, with transparent delivery. Start a sprint.',
    keywords: 'agile software development services, project sprints, agile development outsourcing, sprint based development, agile software sprints, agile engineering team',
  },
  '/services/it-outsourcing': {
    title: 'IT Outsourcing and IT Consulting Services | Brown Tech',
    description: 'IT outsourcing and consulting from Brown Tech: extended development teams, managed IT and strategic advice for growing businesses. Talk to us.',
    keywords: 'it outsourcing, it consulting, it outsourcing services, it consulting company, outsource it team, managed it services, extended development team, it staff outsourcing, technology consulting, it team extension, outsourcing it services for businesses, it consulting firm USA, software development outsourcing, dedicated it team outsourcing, it outsourcing company',
  },
  '/services/gohighlevel-expert': {
    title: 'GoHighLevel Expert and GHL Agency Services | Brown Tech',
    description: 'Hire a dedicated GoHighLevel expert team for GHL automation, workflows, funnels, integrations and ongoing support. Get a proposal from Brown Tech.',
    keywords: 'gohighlevel expert, hire gohighlevel expert, gohighlevel agency, ghl consultant, gohighlevel developer',
  },
  '/faq': {
    title: 'FAQ | GoHighLevel, AI and Software Projects | Brown Tech',
    description: 'Answers from Brown Tech about GoHighLevel, AI voice agents and software projects: how we scope work, timelines, security and support after launch.',
    keywords: 'FAQ, frequently asked questions, AI solutions, cloud services, web development, mobile development, technology consulting, Brown Tech',
  },
  '/products': {
    title: 'AI Sales Agent, CRM and SaaS Products | Brown Tech',
    description: 'Brown Tech products: an AI sales agent, an advanced CRM and a custom SaaS platform, each scoped to the workflow you already run. Ask for a quote.',
    keywords: 'Brown Tech products, AI sales agent, CRM software, SaaS platform, software products',
  },
  '/services/custom-software-development': {
    title: 'Custom Software Development Services | Brown Tech',
    description: 'Brown Tech builds custom web apps, mobile apps, SaaS platforms, APIs and AI powered software around how your business works. Get a quote.',
    keywords: 'custom software development, software development company, custom software development services, custom software development USA, software development company USA, bespoke software development, enterprise software development, custom web application development, saas software development company, ai software development services, mobile app development company, custom crm development, erp software development',
  },
  '/sitemap': {
    title: 'Site Directory | Brown Tech',
    description: 'A directory of Brown Tech pages: GoHighLevel and AI services, software capabilities, products, the blog and contact details.',
    keywords: 'sitemap, site directory, Brown Tech pages, technology services',
  },
  '/ai-city': {
    title: 'AI City Demo | Brown Tech',
    description: 'An illustrated AI city scene from Brown Tech.',
    keywords: 'AI city, Brown Tech demo',
  },
};

function normalize(pathname: string): string {
  if (!pathname) return '/';
  const clean = pathname.split('?')[0].split('#')[0];
  return clean.endsWith('/') && clean !== '/' ? clean.slice(0, -1) : clean;
}

export function getFallbackSEO(pathname: string): FallbackSEO {
  const path = normalize(pathname);
  if (path.startsWith('/blog/')) {
    return {
      title: 'Blog | Brown Tech',
      description: 'Latest articles and insights from our team.'
    };
  }
  if (FALLBACKS[path]) return FALLBACKS[path];

  return {
    title: 'Brown Tech | GoHighLevel, AI Voice Agents and Software',
    description: 'Brown Tech builds GoHighLevel systems, AI voice agents and custom software.'
  };
}
